import React, { useEffect, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import { DATABASE_API_URL } from '../../apiConfig';

let savedSessionPnLStatsCache = {};

export default function SavedStrategies({ refreshTrigger, optionChainData, selectedExpiry, onStrategiesDeleted, niftyData }) {
    const [strategies, setStrategies] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [sessionPnLStats, setSessionPnLStats] = useState(savedSessionPnLStatsCache);
    const [selectedStrategyIds, setSelectedStrategyIds] = useState([]);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);


    const fetchSavedStrategies = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await fetch(`${DATABASE_API_URL}/api/strategy`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch strategies: ${response.statusText}`);
            }

            const data = await response.json();
            setStrategies(data.data || []);
            setSelectedStrategyIds([]);
        } catch (err) {
            console.error('Error fetching strategies:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSavedStrategies();
    }, []);

    useEffect(() => {
        if (refreshTrigger > 0) {
            fetchSavedStrategies();
        }
    }, [refreshTrigger]);

    const resetSessionTracking = () => {
        savedSessionPnLStatsCache = {};
        setSessionPnLStats({});
    };

    const toggleStrategySelection = (strategyId) => {
        setSelectedStrategyIds((prev) =>
            prev.includes(strategyId)
                ? prev.filter((id) => id !== strategyId)
                : [...prev, strategyId]
        );
    };

    const toggleSelectAll = () => {
        setSelectedStrategyIds((prev) =>
            prev.length === strategies.length ? [] : strategies.map((strategy) => strategy.id)
        );
    };

    const toggleStrategyExpansion = (strategyId) => {
        setExpandedId(expandedId === strategyId ? null : strategyId);
    };

    const deleteSelectedStrategies = async () => {
        if (selectedStrategyIds.length === 0) return;

        const shouldDelete = window.confirm(
            `Delete ${selectedStrategyIds.length} selected strateg${selectedStrategyIds.length === 1 ? 'y' : 'ies'}?`
        );

        if (!shouldDelete) return;

        setIsBulkDeleting(true);

        try {
            const idsToDelete = [...selectedStrategyIds];
            const results = [];

            for (const strategyId of idsToDelete) {
                try {
                    const response = await fetch(`${DATABASE_API_URL}/api/strategy/${strategyId}`, {
                        method: 'DELETE',
                        headers: { 'Content-Type': 'application/json' }
                    });

                    if (!response.ok) {
                        throw new Error(`Failed to delete strategy ${strategyId}`);
                    }

                    results.push({ status: 'fulfilled', value: strategyId });
                } catch (error) {
                    console.error(`Error deleting strategy ${strategyId}:`, error);
                    results.push({ status: 'rejected', reason: error });
                }
            }

            const deletedIds = results
                .filter((result) => result.status === 'fulfilled')
                .map((result) => result.value);

            if (deletedIds.length > 0) {
                const deletedIdSet = new Set(deletedIds);
                const deletedStrategies = strategies.filter((strategy) => deletedIdSet.has(strategy.id));
                setStrategies((prev) => prev.filter((strategy) => !deletedIdSet.has(strategy.id)));
                setSelectedStrategyIds((prev) => prev.filter((id) => !deletedIdSet.has(id)));

                if (deletedIdSet.has(expandedId)) {
                    setExpandedId(null);
                }

                deletedIds.forEach((strategyId) => {
                    delete savedSessionPnLStatsCache[strategyId];
                });

                setSessionPnLStats((prev) => {
                    const next = { ...prev };
                    deletedIds.forEach((strategyId) => {
                        delete next[strategyId];
                    });
                    return next;
                });

                if (onStrategiesDeleted) {
                    onStrategiesDeleted(deletedStrategies);
                }
            }

            const failedCount = results.length - deletedIds.length;
            if (failedCount > 0) {
                alert(`${deletedIds.length} deleted, ${failedCount} failed. Please try again.`);
            } else {
                alert('Selected strategies deleted successfully');
            }

            await fetchSavedStrategies();
        } catch (err) {
            console.error('Error deleting selected strategies:', err);
            alert('Failed to delete selected strategies');
        } finally {
            setIsBulkDeleting(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        try {
            return new Date(dateString).toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    const getSignalColor = (signal) => {
        switch (signal) {
            case 'STRONG BUY':
                return 'bg-green-900 text-green-100 border-green-700';
            case 'BUY':
                return 'bg-emerald-900 text-emerald-100 border-emerald-700';
            case 'NEUTRAL':
                return 'bg-blue-900 text-blue-100 border-blue-700';
            case 'AVOID':
                return 'bg-red-900 text-red-100 border-red-700';
            default:
                return 'bg-gray-700 text-gray-100 border-gray-600';
        }
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return 'N/A';
        return `${value >= 0 ? '₹' : '-₹'}${Math.abs(value).toFixed(2)}`;
    };

    const buildPremiumLookup = (strategy) => {
        let isSameExpiry = false;

        try {
            if (selectedExpiry && strategy.expiry_date) {
                isSameExpiry =
                    new Date(selectedExpiry).toISOString().split('T')[0] ===
                    new Date(strategy.expiry_date).toISOString().split('T')[0];
            }
        } catch (e) { }

        if (!optionChainData?.chains || !isSameExpiry) {
            return null;
        }

        const getPremium = (strike, type) => {
            const chain = optionChainData.chains.find((item) => item.strike_price === strike);
            if (!chain) return null;
            const opt = type === 'CE' ? chain.call_options : chain.put_options;
            if (!opt) return null;
            if (opt.ltp !== null && opt.ltp !== undefined && opt.ltp !== 0) return opt.ltp;
            if (opt.close_price !== null && opt.close_price !== undefined && opt.close_price !== 0) return opt.close_price;
            return 0.05;
        };

        return {
            buyCall: getPremium(strategy.buy_call_strike, 'CE'),
            buyPut: getPremium(strategy.buy_put_strike, 'PE'),
            sellCall: getPremium(strategy.sell_call_strike, 'CE'),
            sellPut: getPremium(strategy.sell_put_strike, 'PE')
        };
    };

    const calculateCurrentPnL = (strategy) => {
        const premiums = buildPremiumLookup(strategy);

        if (
            !premiums ||
            premiums.buyCall === null ||
            premiums.buyPut === null ||
            premiums.sellCall === null ||
            premiums.sellPut === null
        ) {
            return null;
        }

        const entryNetPremium =
            (strategy.sell_call_premium + strategy.sell_put_premium) -
            (strategy.buy_call_premium + strategy.buy_put_premium);
        const currentNetPremium =
            (premiums.sellCall + premiums.sellPut) - (premiums.buyCall + premiums.buyPut);

        return (entryNetPremium - currentNetPremium) * 100;
    };

    useEffect(() => {
        if (!strategies.length) {
            savedSessionPnLStatsCache = {};
            setSessionPnLStats({});
            return;
        }

        setSessionPnLStats((prevStats) => {
            const nextStats = {};

            strategies.forEach((strategy) => {
                const currentPnL = calculateCurrentPnL(strategy);
                const previousStats = savedSessionPnLStatsCache[strategy.id] || prevStats[strategy.id] || {
                    peakPnL: 0,
                    troughPnL: 0,
                    currentPnL: null,
                    source: 'live_only'
                };

                let peakPnL = previousStats.peakPnL ?? 0;
                let troughPnL = previousStats.troughPnL ?? 0;

                if (currentPnL !== null) {
                    if (currentPnL > peakPnL) {
                        peakPnL = currentPnL;
                    }

                    if (currentPnL < troughPnL) {
                        troughPnL = currentPnL;
                    }
                }

                nextStats[strategy.id] = {
                    currentPnL,
                    peakPnL,
                    troughPnL,
                    source: 'live_only'
                };
            });

            savedSessionPnLStatsCache = nextStats;
            return nextStats;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [strategies, optionChainData, selectedExpiry]);

    if (isLoading) {
        return (
            <div className="bg-gray-800/50 backdrop-blur-xl rounded-lg p-6 border border-gray-700/50">
                <h2 className="text-2xl font-bold text-white mb-4">Saved Strategies</h2>
                <div className="text-gray-400 text-center py-8">Loading strategies...</div>
            </div>
        );
    }

    return (
        <div className="bg-gray-800/50 backdrop-blur-xl rounded-lg p-6 border border-gray-700/50 mt-8">
            <div className="flex flex-col gap-4 mb-6 md:flex-row md:items-center md:justify-between">
                <h2 className="text-2xl font-bold text-white">Saved Strategies</h2>
                <div className="flex flex-wrap items-center gap-3">
                    {strategies.length > 0 && (
                        <button
                            onClick={toggleSelectAll}
                            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                        >
                            {selectedStrategyIds.length === strategies.length ? 'Clear All' : 'Select All'}
                        </button>
                    )}
                    {selectedStrategyIds.length > 0 && (
                        <button
                            onClick={deleteSelectedStrategies}
                            disabled={isBulkDeleting}
                            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-900 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
                        >
                            {isBulkDeleting ? 'Deleting...' : `Delete Selected (${selectedStrategyIds.length})`}
                        </button>
                    )}
                    <button
                        onClick={() => {
                            resetSessionTracking();
                            fetchSavedStrategies();
                        }}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                        🔄 Refresh
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-red-900/30 border border-red-700 text-red-200 p-4 rounded-lg mb-6">
                    ⚠️ {error}
                </div>
            )}

            {strategies.length === 0 ? (
                <div className="text-gray-400 text-center py-8">
                    No saved strategies yet. Create one using the form above!
                </div>
            ) : (
                <div className="space-y-4">
                    {strategies.map((strategy) => {
                        const livePremiums = buildPremiumLookup(strategy);
                        const currentBuyCall = livePremiums?.buyCall ?? null;
                        const currentBuyPut = livePremiums?.buyPut ?? null;
                        const currentSellCall = livePremiums?.sellCall ?? null;
                        const currentSellPut = livePremiums?.sellPut ?? null;
                        const sessionStats = sessionPnLStats[strategy.id] || {
                            peakPnL: 0,
                            troughPnL: 0,
                            currentPnL: null,
                            source: 'live_only'
                        };
                        const currentPnL = sessionStats.currentPnL;

                        return (
                            <div
                                key={strategy.id}
                                className={`bg-gray-700/30 border rounded-lg overflow-hidden hover:border-gray-500 transition-all ${selectedStrategyIds.includes(strategy.id) ? 'border-blue-400/80' : 'border-gray-600/50'}`}
                            >
                                <div
                                    onClick={() => toggleStrategyExpansion(strategy.id)}
                                    className="p-4 cursor-pointer select-none hover:bg-gray-800 transition-colors"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-5 flex-1 min-w-0">
                                            <button
                                                type="button"
                                                role="checkbox"
                                                aria-checked={selectedStrategyIds.includes(strategy.id)}
                                                aria-label={`Select strategy ${strategy.id}`}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    toggleStrategySelection(strategy.id);
                                                }}
                                                className={`group relative flex h-5 w-5 shrink-0 cursor-pointer select-none items-center justify-center rounded-md border backdrop-blur-md transition-all duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-blue-300/70 focus:ring-offset-2 focus:ring-offset-gray-900 active:scale-95 [&_*]:pointer-events-none ${selectedStrategyIds.includes(strategy.id)
                                                    ? 'border-blue-300/60 bg-gradient-to-br from-blue-500/75 via-indigo-500/75 to-purple-600/75 shadow-[0_0_12px_rgba(96,165,250,0.32)]'
                                                    : 'border-white/10 bg-white/[0.035] shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] hover:border-blue-300/35 hover:bg-white/[0.07]'
                                                    }`}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <span className={`absolute inset-0 rounded-md bg-gradient-to-br from-white/20 to-transparent opacity-0 transition-opacity duration-200 ${selectedStrategyIds.includes(strategy.id) ? 'opacity-100' : 'group-hover:opacity-70'}`} />
                                                <CheckIcon
                                                    className={`relative h-3.5 w-3.5 text-white transition-all duration-200 ${selectedStrategyIds.includes(strategy.id)
                                                        ? 'scale-100 opacity-100'
                                                        : 'scale-50 opacity-0'
                                                        }`}
                                                />
                                            </button>
                                            <div className="flex-1 min-w-0 pl-1">
                                                <div className="flex items-center gap-3">
                                                    <span className="text-gray-300 font-mono text-sm">
                                                        {strategy.id?.substring(0, 20)}...
                                                    </span>
                                                    <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${getSignalColor(strategy.signal)}`}>
                                                        {strategy.signal || 'N/A'}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-gray-500 mt-1">
                                                    {formatDate(strategy.createdAt)}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex gap-6 text-right">
                                            <div className="text-right">
                                                <div className={`text-lg font-bold ${currentPnL !== null ? (currentPnL >= 0 ? 'text-green-400' : 'text-red-400') : 'text-gray-500'}`}>
                                                    {currentPnL !== null ? formatCurrency(currentPnL) : 'N/A'}
                                                </div>
                                                <div className={`text-xs ${currentPnL !== null ? 'text-blue-400 animate-pulse' : 'text-gray-500'}`}>Current P&L</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-lg font-bold text-white">
                                                    {strategy.max_profit >= 0 ? '₹' : '-₹'}
                                                    {Math.abs(strategy.max_profit || 0).toFixed(2)}
                                                </div>
                                                <div className="text-xs text-gray-400">Max Profit</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {expandedId === strategy.id && (
                                    <div className="bg-gray-700/30 border-t border-gray-600/50 p-4 space-y-4">
                                        <div className="grid grid-cols-2 gap-4 pb-4 border-b border-gray-700">
                                            <div>
                                                <div className="text-xs text-gray-400 uppercase">Selected Date</div>
                                                <div className="text-sm text-white font-mono">
                                                    {strategy.selected_date || '—'}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-gray-400 uppercase">Expiry Date</div>
                                                <div className="text-sm text-white font-mono">
                                                    {strategy.expiry_date || '—'}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 pb-4 border-b border-gray-700">
                                            <div className="space-y-3">
                                                <div>
                                                    <div className="text-xs text-green-400 uppercase font-semibold">Buy Call</div>
                                                    <div className="text-sm text-white">
                                                        Strike: <span className="font-mono font-bold">{strategy.buy_call_strike}</span>
                                                    </div>
                                                    <div className="text-xs text-gray-400">
                                                        Entry: ₹{(strategy.buy_call_premium || 0).toFixed(2)}
                                                    </div>
                                                    <div className="text-xs text-blue-300">
                                                        Current: {currentBuyCall !== null ? `₹${currentBuyCall.toFixed(2)}` : 'N/A'}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-xs text-blue-400 uppercase font-semibold">Buy Put</div>
                                                    <div className="text-sm text-white">
                                                        Strike: <span className="font-mono font-bold">{strategy.buy_put_strike}</span>
                                                    </div>
                                                    <div className="text-xs text-gray-400">
                                                        Entry: ₹{(strategy.buy_put_premium || 0).toFixed(2)}
                                                    </div>
                                                    <div className="text-xs text-blue-300">
                                                        Current: {currentBuyPut !== null ? `₹${currentBuyPut.toFixed(2)}` : 'N/A'}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="space-y-3">
                                                <div>
                                                    <div className="text-xs text-red-400 uppercase font-semibold">Sell Call</div>
                                                    <div className="text-sm text-white">
                                                        Strike: <span className="font-mono font-bold">{strategy.sell_call_strike}</span>
                                                    </div>
                                                    <div className="text-xs text-gray-400">
                                                        Entry: ₹{(strategy.sell_call_premium || 0).toFixed(2)}
                                                    </div>
                                                    <div className="text-xs text-blue-300">
                                                        Current: {currentSellCall !== null ? `₹${currentSellCall.toFixed(2)}` : 'N/A'}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-xs text-orange-400 uppercase font-semibold">Sell Put</div>
                                                    <div className="text-sm text-white">
                                                        Strike: <span className="font-mono font-bold">{strategy.sell_put_strike}</span>
                                                    </div>
                                                    <div className="text-xs text-gray-400">
                                                        Entry: ₹{(strategy.sell_put_premium || 0).toFixed(2)}
                                                    </div>
                                                    <div className="text-xs text-blue-300">
                                                        Current: {currentSellPut !== null ? `₹${currentSellPut.toFixed(2)}` : 'N/A'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between gap-3 -mb-1">
                                            <div className="flex items-center gap-3">
                                                <div className="text-sm text-white font-semibold">Performance Summary</div>
                                                <div className="flex items-center gap-2 text-xs text-emerald-300 font-semibold">
                                                    <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-400 animate-pulse" />
                                                    Live
                                                </div>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-4 border-b border-gray-700">
                                            <div className="bg-green-900/20 border border-green-700/40 rounded-lg p-3">
                                                <div className="text-xs text-green-300 uppercase font-semibold">Max Profit Seen</div>
                                                <div className={`text-lg font-bold ${sessionStats.peakPnL > 0 ? 'text-emerald-400' : 'text-gray-400'}`}>
                                                    {formatCurrency(sessionStats.peakPnL)}
                                                </div>
                                            </div>
                                            <div className="bg-red-900/20 border border-red-700/40 rounded-lg p-3">
                                                <div className="text-xs text-red-300 uppercase font-semibold">Max Loss Seen</div>
                                                <div className={`text-lg font-bold ${sessionStats.troughPnL < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                                                    {formatCurrency(sessionStats.troughPnL)}
                                                </div>
                                            </div>
                                            <div className="bg-blue-900/20 border border-blue-700/40 rounded-lg p-3">
                                                <div className={`text-xs uppercase font-semibold ${currentPnL !== null ? 'text-blue-300 animate-pulse' : 'text-gray-400'}`}>
                                                    Current Realized/Unrealized P&L
                                                </div>
                                                <div className={`text-lg font-bold ${currentPnL !== null ? (currentPnL >= 0 ? 'text-green-400' : 'text-red-400') : 'text-gray-500'}`}>
                                                    {formatCurrency(currentPnL)}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-4 gap-4 pb-4 border-b border-gray-700">
                                            <div>
                                                <div className="text-xs text-gray-400 uppercase">Max Profit</div>
                                                <div className="text-lg font-bold text-green-400">
                                                    ₹{(strategy.max_profit || 0).toFixed(2)}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-gray-400 uppercase">Max Loss</div>
                                                <div className="text-lg font-bold text-red-400">
                                                    ₹{(strategy.max_loss || 0).toFixed(2)}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-gray-400 uppercase">Risk/Reward Ratio</div>
                                                <div className="text-lg font-bold text-blue-400">
                                                    1 : {(strategy.risk_reward_ratio || 0).toFixed(2)}
                                                </div>
                                            </div>
                                            <div>
                                                <div className={`text-xs uppercase font-bold ${currentPnL !== null ? 'text-blue-400 animate-pulse' : 'text-gray-500'}`}>Current P&L</div>
                                                <div className={`text-lg font-bold ${currentPnL !== null ? (currentPnL >= 0 ? 'text-green-400' : 'text-red-400') : 'text-gray-500'}`}>
                                                    {currentPnL !== null ? formatCurrency(currentPnL) : 'N/A'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
